import React from 'react';
import { Box, Typography, Stack, Tooltip, Chip } from '@mui/material';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import { workProfileSectionTitleSx } from '../../constants/workProfileSectionTitle';

interface Skill {
    skill: string;
    years_of_experience: number;
    description?: string;
    expertise: 'beginner' | 'mid-level' | 'expert';
}

interface SkillsListProps {
    skills: Skill[];
    themeStyle: ThemeStyle;
    displayAsChips?: boolean;
    fullWidth?: boolean;
    header?: string;
}

export const renderSkillLevel = (expertise: string | { label: string; value: string }, themeStyle: ThemeStyle) => {
    const levelMap = {
        beginner: 1,
        'mid-level': 2,
        expert: 3,
    };

    // Extract value from object or use the string directly
    const expertiseValue = typeof expertise === 'string' ? expertise : expertise?.value;
    const expertiseLabel = typeof expertise === 'string' ? expertise : expertise?.label;

    const filledDots = levelMap[expertiseValue as keyof typeof levelMap] || 0;

    return (
        <Box sx={{ display: 'flex', gap: '4px', cursor: 'pointer', alignItems: 'center' }}>
            {[1, 2, 3].map(i => (
                <Tooltip
                    key={i}
                    title={i === filledDots ? expertiseLabel.charAt(0).toUpperCase() + expertiseLabel.slice(1) : ''}
                    arrow
                    placement="top">
                    <Box
                        sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor:
                                i <= filledDots ? theme[themeStyle].highlight : 'rgba(255,255,255,0.58)',
                            transition: 'background-color 0.3s',
                            borderWidth: '0.8px',
                        }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
};

const SkillCard: React.FC<SkillsListProps> = ({ skills, themeStyle, displayAsChips, fullWidth, header }) => {
    if (!skills || skills.length === 0) return null;

    return (
        <Box sx={{ width: fullWidth ? '100%' : 'auto', mt: 2 }}>
            <Typography sx={{ ...workProfileSectionTitleSx(themeStyle), mb: 1.5 }}>{header ?? 'Skills'}</Typography>
            {displayAsChips ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {skills.map((skill, index) => (
                        // Keep same data; restyle to the new dark + yellow badge language.
                        <Chip
                            key={index}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].highlight,
                                            paddingRight: '2px',
                                        }}>
                                        {skill.skill}
                                    </span>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: 'rgba(255,255,255,0.75)',
                                            fontSize: '10px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}>
                                        {skill.expertise === 'mid-level'
                                            ? 'Intermediate'
                                            : skill.expertise.charAt(0).toUpperCase() + skill.expertise.slice(1)}
                                    </Typography>
                                    {renderSkillLevel(skill.expertise, themeStyle)}
                                </Box>
                            }
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].tag_white,
                                backgroundColor: '#20201f',
                                border: `1px solid ${theme[themeStyle].highlight}66`,
                                borderRadius: '999px',
                                textTransform: 'capitalize',
                                padding: '6px 8px',
                                '& .MuiChip-label': { px: 1.25 },
                            }}
                        />
                    ))}
                </Stack>
            ) : (
                <Stack spacing={1}>
                    {skills.map((skill, index) => (
                        <Box key={index} sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    textTransform: 'capitalize',
                                }}>
                                <strong>{skill.skill}</strong> - {skill.years_of_experience} years
                            </Typography>
                            {skill.description && (
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                    }}>
                                    {skill.description}
                                </Typography>
                            )}
                            <Box sx={{ mt: 1 }}>{renderSkillLevel(skill.expertise, themeStyle)}</Box>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export { SkillCard };
